import React from 'react';
import { Card as HeroCard, CardHeader as HeroCardHeader, CardBody as HeroCardBody, CardFooter as HeroCardFooter } from '@heroui/react';
import { cn } from '@/shared/lib/utils';

export interface CardProps {
  className?: string;
  children?: React.ReactNode;
  hover?: boolean;
  isPressable?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, hover = false, isPressable, children }, ref) => {
    return (
      <HeroCard
        ref={ref}
        isPressable={isPressable || hover}
        className={cn(
          'bg-white border border-gray-300 rounded-lg shadow-none',
          hover && 'border-gray-300 hover:border-gray-400 hover:bg-gray-50/50 transition-all duration-300',
          className
        )}
      >
        {children}
      </HeroCard>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <HeroCardHeader
    className={cn('p-6 border-b border-gray-100', className)}
    {...props}
  />
);

CardHeader.displayName = 'CardHeader';

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <HeroCardBody
    className={cn('p-6', className)}
    {...props}
  />
);

CardBody.displayName = 'CardBody';

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <HeroCardFooter
    className={cn('p-6 border-t border-gray-100', className)}
    {...props}
  />
);

CardFooter.displayName = 'CardFooter';

// Backwards compatibility - CardContent alias
export const CardContent = CardBody;

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-semibold text-lg', className)}
    {...props}
  />
));

CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-gray-500 text-sm mt-1 ml-2', className)}
    {...props}
  />
));

CardDescription.displayName = 'CardDescription';